import type { TFunction } from 'i18next';
import { Check, Circle } from 'lucide-react';
import { Trans } from 'react-i18next';
import { ToggleChip } from '@bill-alps/ui';
import {
  ALL_BILLING_MODULES,
  normalizeModuleSet,
} from '@bill-alps/backend/shared/billing/plans';
import {
  type BillingInterval,
  type BillingModuleKey,
  type Plan,
} from '@/lib/billing/billing-package';
import {
  type Feature,
  getFeatures,
  priceFor,
} from '@/lib/billing/plan-features';

const PLAN_OPTIONS: Plan[] = ['free', 'team', 'business'];
const INTERVAL_OPTIONS: BillingInterval[] = ['month', 'year'];

function CircleDot({ color = 'text-riso-pink' }: { color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`lucide lucide-circle-dot h-6 w-6 ${color}`}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <circle cx="12" cy="12" r="5" fill="currentColor"></circle>
    </svg>
  );
}

function accentClasses(plan: Plan, selected: boolean) {
  if (selected && plan === 'team') {
    return 'border-riso-blue/45 bg-riso-blue-light/45 ring-1 ring-riso-blue/15 shadow-hard-blue hover:border-riso-blue/60 hover:shadow-hard-blue-lg focus-visible:ring-riso-blue';
  }
  if (selected) {
    return 'border-riso-teal/45 bg-riso-teal-light/45 ring-1 ring-riso-teal/15 shadow-hard-teal hover:border-riso-teal/60 hover:shadow-hard-teal-lg focus-visible:ring-riso-teal';
  }
  return plan === 'team'
    ? 'border-border/80 bg-card shadow-hard-sm hover:border-riso-blue/30 hover:shadow-hard-blue focus-visible:ring-riso-blue'
    : 'border-border/80 bg-card shadow-hard-sm hover:border-riso-teal/30 hover:shadow-hard-teal focus-visible:ring-riso-teal';
}

function selectedIcon(plan: Plan, selected: boolean) {
  if (!selected) {
    return <Circle className="h-5 w-5 text-muted-foreground/45" />;
  }
  return (
    <CircleDot color={plan === 'team' ? 'text-riso-blue' : 'text-riso-teal'} />
  );
}

function featureText(t: TFunction, plan: Plan, feature: Feature) {
  const values = {
    ...feature.params,
    ...(feature.includedPlan
      ? { plan: t(`account:plan.${feature.includedPlan}`) }
      : {}),
  };

  return (
    <Trans
      t={t}
      i18nKey={`account:plan.features.${feature.key}`}
      values={values}
      components={{
        emphasis:
          plan === 'team' ? (
            <span className="box-decoration-clone rounded-full bg-riso-blue-light px-1.5 py-0.5 font-bold text-riso-blue" />
          ) : (
            <strong className="font-bold text-foreground" />
          ),
      }}
    />
  );
}

function FeatureRow({
  t,
  plan,
  feature,
}: {
  t: TFunction;
  plan: Plan;
  feature: Feature;
}) {
  return (
    <li
      className="flex items-start gap-2.5 text-sm leading-relaxed"
      data-testid={`plan-feature-${plan}-${feature.key}`}
    >
      <Check
        className={`mt-0.5 h-4 w-4 shrink-0 stroke-[2.5] ${
          plan === 'team' ? 'text-riso-blue' : 'text-riso-teal'
        }`}
        aria-hidden="true"
      />
      <span>{featureText(t, plan, feature)}</span>
    </li>
  );
}

/** Two decimals only when needed: 24.17, never 15.00. */
export function formatChf(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

/**
 * The price line under a plan name: monthly price, or the yearly price with
 * its per-month equivalent so the two intervals compare at a glance.
 */
export function priceLabel(
  t: TFunction,
  plan: Plan,
  modules: readonly BillingModuleKey[],
  interval: BillingInterval,
): string {
  const price = priceFor(plan, modules, interval);
  if (interval === 'year' && plan !== 'free') {
    return t('account:plan.pricePerYear', {
      price: formatChf(price),
      monthly: formatChf(price / 12),
    });
  }
  return t('account:plan.pricePerMonth', { price: formatChf(price) });
}

/**
 * « Choisis tes modules, choisis ta taille » — three module chips
 * (multi-select, at least one), a monthly/yearly switch, and the three sizes
 * priced for the current selection.
 */
export function PlanPicker({
  t,
  selectedPlan,
  selectedModules,
  selectedInterval,
  onSelectPlan,
  onSelectModules,
  onSelectInterval,
  centerModules = false,
}: {
  t: TFunction;
  selectedPlan: Plan;
  selectedModules: readonly BillingModuleKey[];
  selectedInterval: BillingInterval;
  onSelectPlan: (plan: Plan) => void;
  onSelectModules: (modules: BillingModuleKey[]) => void;
  onSelectInterval: (interval: BillingInterval) => void;
  centerModules?: boolean;
}) {
  const toggleModule = (module: BillingModuleKey) => {
    const selected = new Set(selectedModules);
    if (selected.has(module)) {
      // Never let the selection go empty: a package must bill something.
      if (selected.size === 1) return;
      selected.delete(module);
    } else {
      selected.add(module);
    }
    onSelectModules(normalizeModuleSet(selected));
  };

  return (
    <div className="space-y-6">
      <div
        className={`flex flex-col gap-3 xl:flex-row xl:items-center ${
          centerModules ? 'xl:justify-center' : 'xl:justify-between'
        }`}
        data-testid="plan-controls"
      >
        <div
          className="inline-flex w-full flex-wrap gap-2 xl:w-auto"
          role="group"
          aria-label={t('account:plan.modulesLabel')}
          data-testid="plan-module-group"
        >
          {ALL_BILLING_MODULES.map((module) => {
            const checked = selectedModules.includes(module);
            // The last selected module reads as required, not unavailable.
            const last = checked && selectedModules.length === 1;
            return (
              <ToggleChip
                key={module}
                tone="navy"
                checked={checked}
                locked={last}
                onCheckedChange={() => toggleModule(module)}
                data-testid={`plan-module-${module}`}
                className="flex-1 max-2xl:h-[34px] max-2xl:gap-2 max-2xl:px-3 max-2xl:text-xs max-2xl:[&>span:first-child]:size-[15px] max-2xl:[&>span:first-child>svg]:size-2.5 xl:flex-none"
              >
                {t(`account:plan.modules.${module}`)}
              </ToggleChip>
            );
          })}
        </div>

        <div
          className="inline-flex w-full rounded-lg border-2 border-border bg-muted/25 p-1 xl:w-auto"
          role="radiogroup"
          aria-label={t('account:plan.intervalLabel')}
          data-testid="plan-interval-group"
        >
          {INTERVAL_OPTIONS.map((interval) => (
            <button
              key={interval}
              type="button"
              role="radio"
              aria-checked={selectedInterval === interval}
              onClick={() => onSelectInterval(interval)}
              data-testid={`plan-interval-${interval}`}
              className={`flex-1 cursor-pointer rounded-md px-3 py-1.5 text-sm font-semibold transition-colors xl:flex-none ${
                selectedInterval === interval
                  ? 'bg-primary text-primary-foreground shadow-hard-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`account:plan.interval.${interval}`)}
              {interval === 'year' && (
                <span
                  data-testid="plan-year-offer"
                  className={`ml-1 text-xs font-bold opacity-80 ${
                    selectedInterval === 'year' ? '' : 'text-riso-teal'
                  }`}
                >
                  ({t('account:plan.interval.yearHint')})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div
        className="grid grid-cols-1 gap-6 xl:grid-cols-3"
        role="radiogroup"
        data-testid="plan-card-grid"
      >
        {PLAN_OPTIONS.map((candidatePlan) => {
          const selected = selectedPlan === candidatePlan;
          const features = getFeatures(candidatePlan, selectedModules);
          const paid = candidatePlan !== 'free';
          const price = priceFor(
            candidatePlan,
            selectedModules,
            selectedInterval,
          );
          return (
            <button
              key={candidatePlan}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${String(t(`account:plan.${candidatePlan}`))} · CHF ${formatChf(price)} ${String(t(`account:plan.pricePeriod.${selectedInterval}`))}`}
              onClick={() => onSelectPlan(candidatePlan)}
              data-testid={`plan-card-${candidatePlan}`}
              className={`relative flex cursor-pointer flex-col rounded-3xl border-2 p-5 pt-7 text-left transition-[border-color,background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:p-6 sm:pt-8 lg:p-7 lg:pt-8 ${accentClasses(candidatePlan, selected)}`}
            >
              {paid && (
                <span
                  className={`absolute -top-3 left-5 max-w-[calc(100%_-_5rem)] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-white shadow-hard-sm max-2xl:text-[10px] sm:left-6 ${
                    candidatePlan === 'team' ? 'bg-riso-blue' : 'bg-riso-teal'
                  }`}
                  data-testid={`plan-badge-${candidatePlan}`}
                >
                  {t(`account:plan.badge.${candidatePlan}`)}
                </span>
              )}
              <span
                className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center sm:right-5 sm:top-5"
                data-testid={`plan-radio-indicator-${candidatePlan}`}
              >
                {selectedIcon(candidatePlan, selected)}
              </span>

              <div className="pr-9">
                <h3 className="font-display text-xl font-bold">
                  {t(`account:plan.${candidatePlan}`)}
                </h3>
              </div>

              <div className="mt-3 flex flex-wrap items-baseline gap-x-1.5">
                <span className="font-display text-sm font-bold uppercase tracking-[0.08em] text-muted-foreground">
                  CHF
                </span>
                <span
                  className="font-display text-4xl font-bold tracking-tight"
                  data-testid={`plan-price-${candidatePlan}`}
                >
                  {formatChf(price)}
                </span>
                <span
                  className="text-sm text-muted-foreground"
                  data-testid={`plan-period-${candidatePlan}`}
                >
                  {t(`account:plan.pricePeriod.${selectedInterval}`)}
                </span>
              </div>

              <div className="mt-1 min-h-12 space-y-1 text-xs leading-relaxed text-muted-foreground">
                {selectedInterval === 'year' && paid && (
                  <p data-testid={`plan-annual-equivalent-${candidatePlan}`}>
                    {t('account:plan.annualEquivalent', {
                      monthly: formatChf(price / 12),
                    })}
                  </p>
                )}
                {paid && (
                  <p data-testid={`plan-pricing-rule-${candidatePlan}`}>
                    {t('account:plan.pricingRule', {
                      one: formatChf(
                        priceFor(candidatePlan, ['invoicing'], 'month'),
                      ),
                      suite: formatChf(
                        priceFor(candidatePlan, ALL_BILLING_MODULES, 'month'),
                      ),
                    })}
                  </p>
                )}
              </div>

              <div className="mt-5 flex-1 space-y-5">
                <ul className="space-y-2.5">
                  {features.global.map((feature) => (
                    <FeatureRow
                      key={feature.key}
                      t={t}
                      plan={candidatePlan}
                      feature={feature}
                    />
                  ))}
                </ul>

                {features.moduleGroups.map(({ module, features: rows }) => (
                  <section
                    key={module}
                    aria-labelledby={`plan-${candidatePlan}-${module}-heading`}
                  >
                    <h4
                      id={`plan-${candidatePlan}-${module}-heading`}
                      className="mb-2.5 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground"
                      data-testid={`plan-feature-heading-${candidatePlan}-${module}`}
                    >
                      {t(`account:plan.modules.${module}`)}
                    </h4>
                    <ul className="space-y-2.5">
                      {rows.map((feature) => (
                        <FeatureRow
                          key={feature.key}
                          t={t}
                          plan={candidatePlan}
                          feature={feature}
                        />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
